import { FlexBox, Icon, IconButton, Text } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { ampli } from 'lib-shared-tracking';
import { type PropsWithChildren, useCallback, useRef, useState } from 'react';

import {
  defaultSectionOrder,
  SidebarSectionId,
} from 'components/conversation/components/sidebar/conversationSidebar.types';
import { reorderArray } from 'components/conversation/components/sidebar/utils/reorder';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useOrderedDragDrop } from 'utils/useOrderedDragDrop';
import { useServerStorage } from 'utils/useServerStorage';

import { useCardSectionVisibility } from '../../hooks/useCardSectionVisibility';
import { useSidebarMode } from '../../hooks/useSidebarMode';
import { VisibilityToggle } from '../visibilityToggle/visibilityToggle';

import styles from './cardSection.module.scss';

type SectionIdWithLegacy = SidebarSectionId | 'legacyIntegration';

type CardSectionProps = PropsWithChildren & {
  sectionId: SectionIdWithLegacy;
  index: number;
  isMinimized?: boolean;
};

const type = Symbol('cardSection');

const idTitleMap: Record<SectionIdWithLegacy, string> = {
  customerInfo: 'Customer Info',
  customIntegration: 'Custom integrations',
  knowledge: 'Knowledge',
  nativeIntegration: 'Native integrations',
  // Remove once the feature flag is obsolete.
  legacyIntegration: 'Integrations',
} as const;

export const CardSection = ({
  sectionId,
  children,
  index,
  isMinimized,
}: CardSectionProps): JSX.Element | null => {
  const flags = useFeatureFlag(['conversation-right-sidebar-visibility']);
  const storage = useServerStorage('user');
  const [isExpanded, setIsExpanded] = useState(true);
  const row = useRef<HTMLDivElement>(null);
  const rowHandle = useRef<HTMLDivElement>(null);

  const sidebarMode = useSidebarMode();

  const handleMove = useCallback(
    ({ dragIndex, hoverIndex }: { dragIndex: number; hoverIndex: number }) => {
      storage.set({
        conversation: (draft) => {
          draft.sidebarSectionOrder = reorderArray(
            storage.data.conversation?.sidebarSectionOrder,
            defaultSectionOrder,
            dragIndex,
            hoverIndex,
          );
        },
      });

      ampli.conversationRightSidebarItemReordered({
        sidebarItemType: 'section',
      });
    },
    [storage],
  );

  const { hiddenItems } = useCardSectionVisibility();
  const isMoveDisabled = hiddenItems.has(sectionId);
  const isMoveDisabledRef = useRef(isMoveDisabled);
  isMoveDisabledRef.current = isMoveDisabled;
  const { isOver } = useOrderedDragDrop({
    type,
    index,
    source: rowHandle,
    target: row,
    preview: row,
    onMove: handleMove,
    triggerSwap: 'drop',
    canDrag: () => !isMoveDisabledRef.current,
  });

  return (
    <div
      ref={row}
      className={classnames(styles.root, {
        [styles.isOver]: isOver,
        [styles.isMinimized]: isMinimized,
      })}
    >
      {!isMinimized ? (
        <FlexBox alignItems="center" gap="300" padding="300 400 300 300">
          {flags.conversationRightSidebarVisibility &&
            sidebarMode === 'customization' && (
              <div
                ref={rowHandle}
                className={classnames(styles.handle, {
                  [styles.disabled]: isMoveDisabled,
                })}
              >
                <Icon name="bars" />
              </div>
            )}
          <FlexBox alignItems="center" justifyContent="space-between" flex="1">
            <Text.Paragraph variant="3" subdued padding="100 0">
              {idTitleMap[sectionId]}
            </Text.Paragraph>
            {flags.conversationRightSidebarVisibility && (
              <FlexBox justifyContent="end" gap="100">
                {sectionId !== 'customerInfo' ? (
                  <VisibilityToggle
                    itemType="section"
                    itemId={sectionId as SidebarSectionId}
                  />
                ) : null}
                <IconButton
                  onClick={() => setIsExpanded((prev) => !prev)}
                  icon={isExpanded ? 'angle-down' : 'angle-right'}
                  variant="tertiary"
                />
              </FlexBox>
            )}
          </FlexBox>
        </FlexBox>
      ) : null}
      {isExpanded && children}
    </div>
  );
};
